import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { auctionAPI } from '../lib/api';
import DomainCard from '../components/DomainCard';
import { Loader2, Gavel } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const CATEGORIES = ['all', 'tech', 'crypto', 'finance', 'ai', 'health', 'gaming'];

export default function AuctionsPage() {
  const { t } = useLanguage();
  const [auctions, setAuctions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState('all');
  const [sort, setSort] = useState('ending_soon');

  useEffect(() => {
    const fetchAuctions = async () => {
      setLoading(true);
      try {
        const params = { sort };
        if (category !== 'all') params.category = category;
        const res = await auctionAPI.list(params);
        setAuctions(res.data.auctions || []);
      } catch { setAuctions([]); }
      setLoading(false);
    };
    fetchAuctions();
  }, [category, sort]);

  return (
    <div className="px-6 md:px-12 max-w-7xl mx-auto py-12 md:py-20" data-testid="auctions-page">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <Gavel className="h-6 w-6 text-[#00E5FF]" />
            <h1 className="text-4xl md:text-5xl font-bold text-white font-['Outfit'] tracking-tight">{t('auction.title')}</h1>
          </div>
          <p className="text-zinc-500">Bid on exclusive domains in real-time auctions</p>
        </div>
        <div className="flex gap-3">
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className="w-44 bg-[#050505] border-zinc-800 text-zinc-300 rounded-sm" data-testid="auction-sort">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#0A0A0A] border-white/10">
              <SelectItem value="ending_soon" className="text-zinc-300 focus:text-[#00E5FF] focus:bg-white/5">Ending Soon</SelectItem>
              <SelectItem value="newest" className="text-zinc-300 focus:text-[#00E5FF] focus:bg-white/5">Newest</SelectItem>
              <SelectItem value="price_desc" className="text-zinc-300 focus:text-[#00E5FF] focus:bg-white/5">Highest Bid</SelectItem>
              <SelectItem value="most_bids" className="text-zinc-300 focus:text-[#00E5FF] focus:bg-white/5">Most Bids</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Category Filter */}
      <div className="flex flex-wrap gap-2 mb-8">
        {CATEGORIES.map(c => (
          <button key={c} onClick={() => setCategory(c)}
            className={`px-4 py-2 text-xs font-medium uppercase tracking-wider rounded-sm border transition-colors ${category === c ? 'border-[#00E5FF] text-[#00E5FF] bg-[#00E5FF]/5' : 'border-zinc-800 text-zinc-500 hover:border-zinc-600'}`}
            data-testid={`auction-cat-${c}`}>
            {c === 'all' ? t('common.all') : c}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-[#00E5FF]" /></div>
      ) : auctions.length === 0 ? (
        <div className="text-center py-20">
          <Gavel className="h-12 w-12 text-zinc-700 mx-auto mb-4" />
          <p className="text-zinc-500">No active auctions right now</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="auctions-grid">
          {auctions.map(a => <DomainCard key={a.id} domain={a} />)}
        </div>
      )}
    </div>
  );
}
